import { useState } from "react";
import type { SyntheticEvent } from "react";
import { cn } from "@/components/ui/utils";

export interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MAX_MESSAGE_LENGTH = 2000;

export function ChatInput({
  onSend,
  disabled = false,
  placeholder = "Type your question...",
}: ChatInputProps) {
  const [input, setInput] = useState("");

  const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (trimmed && !disabled && trimmed.length <= MAX_MESSAGE_LENGTH) {
      onSend(trimmed);
      setInput("");
    }
  };

  const remainingChars = MAX_MESSAGE_LENGTH - input.length;
  const isNearLimit = remainingChars < 50;
  const isOverLimit = remainingChars < 0;

  return (
    <div className="border-t border-gray-200 dark:border-gray-700">
      <form onSubmit={handleSubmit} className="flex gap-2 p-4">
        <div className="flex-1 relative">
          <input
            type="text"
            value={input}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= MAX_MESSAGE_LENGTH) {
                setInput(value);
              }
            }}
            disabled={disabled}
            placeholder={placeholder}
            maxLength={MAX_MESSAGE_LENGTH}
            className={cn(
              "w-full px-4 py-2 rounded-lg border",
              isOverLimit
                ? "border-red-500 dark:border-red-500"
                : "border-gray-300 dark:border-gray-600",
              "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100",
              "focus:outline-none focus:ring-2 focus:ring-blue-500",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          />
          {isNearLimit && (
            <div
              className={cn(
                "absolute bottom-1 right-2 text-xs",
                isOverLimit ? "text-red-500" : "text-gray-500 dark:text-gray-400"
              )}
            >
              {remainingChars}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={disabled || !input.trim() || isOverLimit}
          className={cn(
            "px-6 py-2 rounded-lg font-medium",
            "bg-blue-600 text-white",
            "hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "transition-colors"
          )}
        >
          Send
        </button>
      </form>
      {isOverLimit && (
        <div className="px-4 pb-2 text-xs text-red-500">
          Message cannot exceed {MAX_MESSAGE_LENGTH} characters
        </div>
      )}
    </div>
  );
}
